import type { DocPerm, MapNode } from '../types';
import { DOC_BEAN_ORDER, DOC_ID_FIELD_BY_KEY, DOC_KINDS } from '../docs';
import { plannedMissingCount } from '../health';
import { CUSTOM_DOC_ROWS_SHOWN, ROW_GAP, ROW_H } from './constants';

/**
 * Extra height a node's expanded documentation rows need, beyond its collapsed box.
 *
 * This function is the reason the whole layout port is possible. It is **deliberately
 * DOM-free**, and the vanilla source says so in as many words: "deterministic (no DOM
 * measurement needed) so the display-layout pass never has to render first."
 *
 * That is the escape hatch from the measure-render-measure circularity, already found once
 * and written down. The port generalises it: if row height can be computed rather than
 * measured, so can everything else, given a size map.
 *
 * Zero for the root, which never expands, and zero when there is nothing to show.
 */
export function docExtraHeight(
  n: MapNode,
  opts: { expanded: boolean; canDocBtn: (perm: DocPerm) => boolean; plannedVisible?: boolean },
): number {
  if (!opts.expanded || n.root) return 0;

  const fixedCount = DOC_BEAN_ORDER.filter((key) => {
    const kind = DOC_KINDS.find((d) => d.key === key)!;
    return opts.canDocBtn(kind.perm) && n[DOC_ID_FIELD_BY_KEY[key]];
  }).length;

  const nCustom = opts.canDocBtn('custom') ? (n.customDocs ?? []).length : 0;
  // Past the cap, the remainder collapses into a single "+N" row rather than growing without
  // bound — so three custom docs need three rows, but ten still need three.
  const customCount = Math.min(nCustom, CUSTOM_DOC_ROWS_SHOWN) + (nCustom > CUSTOM_DOC_ROWS_SHOWN ? 1 : 0);

  // PLANNED-but-missing dotted pills add rows too (V0.42) — the SAME count `beanRowsOf` appends
  // and `expandedRows` gates on, and only where they are SHOWN (`plannedVisible`, from the same
  // `showPlanned` predicate). Reserve their height or the revealed rows overflow the box.
  const planned = opts.plannedVisible ? plannedMissingCount(n, opts.canDocBtn) : 0;

  const rows = fixedCount + customCount + planned;
  if (rows < 1) return 0;
  return rows * ROW_H + (rows - 1) * ROW_GAP;
}
